import type {
  BulkMarkingArgs,
  BulkMarkingResponseDto,
  DeleteItemArgs,
  GetItemHistoryArgs,
  ItemHistoryResponseDto,
  PreviewCalcArgs,
  PreviewCalcResponseDto,
  UpsertItemArgs,
  UpsertItemResponseDto,
} from "@/@types/price-markdown-selection";
import { Tags } from "@/enum/tags";
import { apiSlice } from "@/services/http";

import { withQueryString } from "./query-string";

export const priceMarkdownItemApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    previewCalc: builder.mutation<PreviewCalcResponseDto, PreviewCalcArgs>({
      query: (body) => ({
        url: `/price-markdown/preview-calc`,
        method: "POST",
        body,
      }),
    }),

    upsertItem: builder.mutation<UpsertItemResponseDto, UpsertItemArgs>({
      query: ({ markdownId, sku, ...body }) => ({
        url: `/price-markdown/selection/drill/${markdownId}/item/${sku}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, _error, { markdownId, sku }) => {
        const realId = result?.markdownId ?? markdownId;
        const tags = [
          { type: Tags.PRICE_MARKDOWN_DRILL, id: markdownId },
          { type: Tags.PRICE_MARKDOWN_VERBA_SUMMARY, id: markdownId },
          { type: Tags.PRICE_MARKDOWN_INDICADORES, id: markdownId },
          { type: Tags.PRICE_MARKDOWN_ITEM_HISTORY, id: sku },
          Tags.PRICE_MARKDOWN_SELECAO,
          Tags.PRICE_MARKDOWN_DRILL,
        ];
        if (realId !== markdownId) {
          tags.push(
            { type: Tags.PRICE_MARKDOWN_DRILL, id: realId },
            { type: Tags.PRICE_MARKDOWN_VERBA_SUMMARY, id: realId },
            { type: Tags.PRICE_MARKDOWN_INDICADORES, id: realId },
          );
        }
        return tags;
      },
    }),

    bulkMarking: builder.mutation<BulkMarkingResponseDto, BulkMarkingArgs>({
      query: ({
        markdownId,
        action,
        turnOn,
        discount,
        skus,
        excludedSkus,
        confirmed,
        family,
        cycle,
        simpleFilters,
        checks,
        advancedFilters,
      }) => ({
        url: `/price-markdown/selection/drill/${markdownId}/bulk-marking`,
        method: "POST",
        body: {
          action,
          turnOn,
          discount,
          skus,
          excludedSkus,
          confirmed,
          family,
          cycle,
          ...simpleFilters,
          ...checks,
          ...advancedFilters,
        },
      }),
      invalidatesTags: (result, _error, { markdownId }) => {
        if (!result?.applied) return [];
        const realId = result.markdownId ?? markdownId;
        return [
          { type: Tags.PRICE_MARKDOWN_DRILL, id: markdownId },
          { type: Tags.PRICE_MARKDOWN_DRILL, id: realId },
          { type: Tags.PRICE_MARKDOWN_VERBA_SUMMARY, id: realId },
          { type: Tags.PRICE_MARKDOWN_INDICADORES, id: markdownId },
          { type: Tags.PRICE_MARKDOWN_INDICADORES, id: realId },
          Tags.PRICE_MARKDOWN_SELECAO,
          Tags.PRICE_MARKDOWN_DRILL,
        ];
      },
    }),

    deleteItem: builder.mutation<void, DeleteItemArgs>({
      query: ({ markdownId, sku }) => ({
        url: `/price-markdown/selection/drill/${markdownId}/item/${sku}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { markdownId, sku }) => [
        { type: Tags.PRICE_MARKDOWN_DRILL, id: markdownId },
        { type: Tags.PRICE_MARKDOWN_VERBA_SUMMARY, id: markdownId },
        { type: Tags.PRICE_MARKDOWN_INDICADORES, id: markdownId },
        { type: Tags.PRICE_MARKDOWN_ITEM_HISTORY, id: sku },
        Tags.PRICE_MARKDOWN_SELECAO,
        Tags.PRICE_MARKDOWN_DRILL,
      ],
    }),

    getItemHistory: builder.query<ItemHistoryResponseDto, GetItemHistoryArgs>({
      query: ({ sku, limit }) => {
        const params = new URLSearchParams();
        if (limit !== undefined) params.set("limit", String(limit));
        return {
          url: withQueryString(`/price-markdown/items/${sku}/history`, params),
          method: "GET",
        };
      },
      providesTags: (_result, _error, { sku }) => [
        { type: Tags.PRICE_MARKDOWN_ITEM_HISTORY, id: sku },
        Tags.PRICE_MARKDOWN_ITEM_HISTORY,
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  usePreviewCalcMutation,
  useUpsertItemMutation,
  useBulkMarkingMutation,
  useDeleteItemMutation,
  useGetItemHistoryQuery,
} = priceMarkdownItemApi;
